import { apiClient } from "./client";
import type { RealizedStatus } from "./types";

export type TimesheetStatus =
  | "OPEN"
  | "SUBMITTED"
  | "VALIDATED"
  | "REJECTED"
  | "PAID";

export type TimesheetTotals = {
  planned_shifts: number;
  realized_shifts: number;
  absent_shifts: number;
  extra_shifts: number;
  deviating_shifts: number;
  total_minutes: number;
  weighted_minutes: number;
  night_minutes: number;
  by_exercise_mode: Record<
    string,
    { label: string; shifts: number; minutes: number; weighted_minutes: number }
  >;
  by_slot_kind: Record<
    string,
    { shifts: number; minutes: number; weighted_minutes: number }
  >;
};

export type TimesheetPeriod = {
  id: string;
  organization_id: string;
  organization_name: string | null;
  practitioner_id: string;
  practitioner_display_name: string | null;
  year: number;
  month: number;
  status: TimesheetStatus;
  summary: string | null;
  submitted_at: string | null;
  validated_at: string | null;
  validated_by_user_id: string | null;
  review_note: string | null;
  paid_at: string | null;
  payroll_reference: string | null;
  /** Recomputed on every read — reflects the planning as it stands today. */
  totals: TimesheetTotals | null;
  /** Frozen at validation — what payroll was actually handed. */
  frozen_totals: TimesheetTotals | null;
};

export type TimesheetLine = {
  assignment_id: string;
  date: string;
  slot_label: string;
  slot_kind: string;
  start_time: string;
  end_time: string;
  exercise_mode_code: string;
  exercise_mode_label: string;
  functional_role_code: string | null;
  realized_status: RealizedStatus;
  minutes: number;
  weighted_minutes: number;
  counts_as_worked: boolean;
  is_night: boolean;
  actual_start_at: string | null;
  actual_end_at: string | null;
  practitioner_summary: string | null;
  manager_note: string | null;
  comment: string | null;
  /** What departs from the forecast: date, slot, practitioner, hours… */
  deviations: string[];
};

export type TimesheetWeek = {
  iso_year: number;
  iso_week: number;
  week_start: string;
  shifts: number;
  planned_shifts: number;
  realized_shifts: number;
  minutes: number;
  weighted_minutes: number;
};

export type TimesheetSheet = {
  period: TimesheetPeriod;
  lines: TimesheetLine[];
  weeks: TimesheetWeek[];
};

// ----------------------------------------------------------------------------
// Self-service
// ----------------------------------------------------------------------------
export async function listMyPeriods(year?: number): Promise<TimesheetPeriod[]> {
  const { data } = await apiClient.get<TimesheetPeriod[]>("/api/v1/me/timesheets", {
    params: year ? { year } : undefined,
  });
  return data;
}

export async function getMySheet(
  organizationId: string,
  year: number,
  month: number,
): Promise<TimesheetSheet> {
  const { data } = await apiClient.get<TimesheetSheet>(
    `/api/v1/me/timesheets/${organizationId}/${year}/${month}`,
  );
  return data;
}

export async function saveMySummary(
  organizationId: string,
  year: number,
  month: number,
  summary: string | null,
): Promise<TimesheetPeriod> {
  const { data } = await apiClient.put<TimesheetPeriod>(
    `/api/v1/me/timesheets/${organizationId}/${year}/${month}/summary`,
    { summary },
  );
  return data;
}

export async function saveMyShiftSummary(
  assignmentId: string,
  practitionerSummary: string | null,
): Promise<TimesheetLine> {
  const { data } = await apiClient.put<TimesheetLine>(
    `/api/v1/me/timesheets/shifts/${assignmentId}/summary`,
    { practitioner_summary: practitionerSummary },
  );
  return data;
}

export async function submitMyPeriod(
  organizationId: string,
  year: number,
  month: number,
  summary?: string | null,
): Promise<TimesheetPeriod> {
  const { data } = await apiClient.post<TimesheetPeriod>(
    `/api/v1/me/timesheets/${organizationId}/${year}/${month}/submit`,
    summary === undefined ? {} : { summary },
  );
  return data;
}

// ----------------------------------------------------------------------------
// Manager
// ----------------------------------------------------------------------------
export async function listOrgPeriods(
  organizationId: string,
  year: number,
  month: number,
  status?: TimesheetStatus,
): Promise<TimesheetPeriod[]> {
  const { data } = await apiClient.get<TimesheetPeriod[]>(
    `/api/v1/timesheets/${organizationId}/${year}/${month}`,
    { params: status ? { status } : undefined },
  );
  return data;
}

export async function getPeriodSheet(periodId: string): Promise<TimesheetSheet> {
  const { data } = await apiClient.get<TimesheetSheet>(
    `/api/v1/timesheets/periods/${periodId}/sheet`,
  );
  return data;
}

export async function validatePeriod(
  periodId: string,
  reviewNote?: string | null,
): Promise<TimesheetPeriod> {
  const { data } = await apiClient.post<TimesheetPeriod>(
    `/api/v1/timesheets/periods/${periodId}/validate`,
    { review_note: reviewNote ?? null },
  );
  return data;
}

export async function rejectPeriod(
  periodId: string,
  reviewNote: string,
): Promise<TimesheetPeriod> {
  const { data } = await apiClient.post<TimesheetPeriod>(
    `/api/v1/timesheets/periods/${periodId}/reject`,
    { review_note: reviewNote },
  );
  return data;
}

export async function reopenPeriod(periodId: string): Promise<TimesheetPeriod> {
  const { data } = await apiClient.post<TimesheetPeriod>(
    `/api/v1/timesheets/periods/${periodId}/reopen`,
    {},
  );
  return data;
}

export async function markPeriodPaid(
  periodId: string,
  payrollReference?: string | null,
): Promise<TimesheetPeriod> {
  const { data } = await apiClient.post<TimesheetPeriod>(
    `/api/v1/timesheets/periods/${periodId}/mark-paid`,
    { payroll_reference: payrollReference ?? null },
  );
  return data;
}

// ----------------------------------------------------------------------------
// CSV exports — served as attachments, so we trigger a browser download.
// ----------------------------------------------------------------------------
async function downloadCsv(url: string, filename: string): Promise<void> {
  const { data } = await apiClient.get<Blob>(url, { responseType: "blob" });
  const href = URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

export function exportPeriodCsv(periodId: string, filename: string): Promise<void> {
  return downloadCsv(`/api/v1/timesheets/periods/${periodId}/sheet.csv`, filename);
}

export function exportOrgCsv(
  organizationId: string,
  year: number,
  month: number,
  onlyValidated = true,
): Promise<void> {
  return downloadCsv(
    `/api/v1/timesheets/${organizationId}/${year}/${month}/export.csv?only_validated=${onlyValidated}`,
    `releves_${year}-${String(month).padStart(2, "0")}.csv`,
  );
}

export function exportMyCsv(
  organizationId: string,
  year: number,
  month: number,
): Promise<void> {
  return downloadCsv(
    `/api/v1/me/timesheets/${organizationId}/${year}/${month}/export.csv`,
    `mon_releve_${year}-${String(month).padStart(2, "0")}.csv`,
  );
}

/** `498` → `8 h 18`. Minutes are the unit everywhere in the API. */
export function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}
